'use client';

import { createClient } from '@/lib/supabase/client';
import { Project, SubProject, Hutang, PembayaranHutang, Piutang, PenerimaanPiutang, ActivityLog } from './mockData';

function getSupabase() {
  return createClient();
}

// ─── Projects ────────────────────────────────────────────────────────────────

function rowToProject(row: any): Project {
  return {
    id: row.id,
    nama: row.nama,
    lokasi: row.lokasi,
    pic: row.pic,
    manager: row.manager,
    tanggalMulai: row.tanggal_mulai,
    targetSelesai: row.target_selesai,
    budget: row.budget,
    status: row.status,
    deskripsi: row.deskripsi,
  };
}

function projectToRow(p: Project) {
  return {
    id: p.id,
    nama: p.nama,
    lokasi: p.lokasi,
    pic: p.pic,
    manager: p.manager,
    tanggal_mulai: p.tanggalMulai,
    target_selesai: p.targetSelesai,
    budget: p.budget,
    status: p.status,
    deskripsi: p.deskripsi,
  };
}

export async function getProjectsDB(): Promise<Project[]> {
  const supabase = getSupabase();
  const { data, error } = await supabase.from('projects').select('*').order('id');
  if (error) { console.error('getProjectsDB error:', error.message); return []; }
  return (data || []).map(rowToProject);
}

export async function addProjectDB(p: Project): Promise<void> {
  const supabase = getSupabase();
  const { error } = await supabase.from('projects').insert(projectToRow(p));
  if (error) throw new Error(error.message);
}

export async function updateProjectDB(id: string, updates: Partial<Project>): Promise<void> {
  const supabase = getSupabase();
  const row: any = {};
  if (updates.nama !== undefined) row.nama = updates.nama;
  if (updates.lokasi !== undefined) row.lokasi = updates.lokasi;
  if (updates.pic !== undefined) row.pic = updates.pic;
  if (updates.manager !== undefined) row.manager = updates.manager;
  if (updates.tanggalMulai !== undefined) row.tanggal_mulai = updates.tanggalMulai;
  if (updates.targetSelesai !== undefined) row.target_selesai = updates.targetSelesai;
  if (updates.budget !== undefined) row.budget = updates.budget;
  if (updates.status !== undefined) row.status = updates.status;
  if (updates.deskripsi !== undefined) row.deskripsi = updates.deskripsi;
  row.updated_at = new Date().toISOString();
  const { error } = await supabase.from('projects').update(row).eq('id', id);
  if (error) throw new Error(error.message);
}

export async function deleteProjectDB(id: string): Promise<void> {
  const supabase = getSupabase();
  const { error } = await supabase.from('projects').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

// ─── Sub Projects ─────────────────────────────────────────────────────────────

function rowToSubProject(row: any): SubProject {
  return {
    id: row.id,
    projectId: row.project_id,
    nama: row.nama,
    pic: row.pic,
    budget: row.budget,
    status: row.status,
  };
}

function subProjectToRow(s: SubProject) {
  return {
    id: s.id,
    project_id: s.projectId,
    nama: s.nama,
    pic: s.pic,
    budget: s.budget,
    status: s.status,
  };
}

export async function getSubProjectsDB(): Promise<SubProject[]> {
  const supabase = getSupabase();
  const { data, error } = await supabase.from('sub_projects').select('*').order('id');
  if (error) { console.error('getSubProjectsDB error:', error.message); return []; }
  return (data || []).map(rowToSubProject);
}

export async function getSubProjectsByProjectDB(projectId: string): Promise<SubProject[]> {
  const supabase = getSupabase();
  const { data, error } = await supabase.from('sub_projects').select('*').eq('project_id', projectId).order('id');
  if (error) { console.error('getSubProjectsByProjectDB error:', error.message); return []; }
  return (data || []).map(rowToSubProject);
}

export async function addSubProjectDB(s: SubProject): Promise<void> {
  const supabase = getSupabase();
  const { error } = await supabase.from('sub_projects').insert(subProjectToRow(s));
  if (error) throw new Error(error.message);
}

export async function updateSubProjectDB(id: string, updates: Partial<SubProject>): Promise<void> {
  const supabase = getSupabase();
  const row: any = {};
  if (updates.projectId !== undefined) row.project_id = updates.projectId;
  if (updates.nama !== undefined) row.nama = updates.nama;
  if (updates.pic !== undefined) row.pic = updates.pic;
  if (updates.budget !== undefined) row.budget = updates.budget;
  if (updates.status !== undefined) row.status = updates.status;
  row.updated_at = new Date().toISOString();
  const { error } = await supabase.from('sub_projects').update(row).eq('id', id);
  if (error) throw new Error(error.message);
}

export async function deleteSubProjectDB(id: string): Promise<void> {
  const supabase = getSupabase();
  const { error } = await supabase.from('sub_projects').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

// ─── Hutang ───────────────────────────────────────────────────────────────────

function rowToHutang(row: any): Hutang {
  return {
    id: row.id,
    projectId: row.project_id,
    subProjectId: row.sub_project_id,
    tanggal: row.tanggal,
    vendor: row.vendor,
    kategori: row.kategori,
    uraian: row.uraian,
    noInvoice: row.no_invoice,
    nilaiTagihan: row.nilai_tagihan,
    totalDibayar: row.total_dibayar,
    sisaHutang: row.sisa_hutang,
    jatuhTempo: row.jatuh_tempo,
    umurHari: row.umur_hari,
    status: row.status,
    pic: row.pic,
    approval: row.approval,
    lampiran: row.lampiran,
    approvalStatus: row.approval_status || 'Pending',
    approvalComment: row.approval_comment || '',
    approvedBy: row.approved_by || '',
    approvedAt: row.approved_at || '',
  };
}

function hutangToRow(h: Hutang) {
  return {
    id: h.id,
    project_id: h.projectId,
    sub_project_id: h.subProjectId,
    tanggal: h.tanggal,
    vendor: h.vendor,
    kategori: h.kategori,
    uraian: h.uraian,
    no_invoice: h.noInvoice,
    nilai_tagihan: h.nilaiTagihan,
    total_dibayar: h.totalDibayar,
    sisa_hutang: h.sisaHutang,
    jatuh_tempo: h.jatuhTempo,
    umur_hari: h.umurHari,
    status: h.status,
    pic: h.pic,
    approval: h.approval,
    lampiran: h.lampiran,
    approval_status: h.approvalStatus || 'Pending',
    approval_comment: h.approvalComment || null,
    approved_by: h.approvedBy || null,
    approved_at: h.approvedAt || null,
  };
}

export async function getHutangListDB(): Promise<Hutang[]> {
  const supabase = getSupabase();
  const { data, error } = await supabase.from('hutang').select('*').order('id');
  if (error) { console.error('getHutangListDB error:', error.message); return []; }
  return (data || []).map(rowToHutang);
}

export async function addHutangDB(h: Hutang): Promise<void> {
  const supabase = getSupabase();
  const { error } = await supabase.from('hutang').insert(hutangToRow(h));
  if (error) throw new Error(error.message);
}

export async function updateHutangDB(id: string, updates: Partial<Hutang>): Promise<void> {
  const supabase = getSupabase();
  const row: any = {};
  if (updates.projectId !== undefined) row.project_id = updates.projectId;
  if (updates.subProjectId !== undefined) row.sub_project_id = updates.subProjectId;
  if (updates.tanggal !== undefined) row.tanggal = updates.tanggal;
  if (updates.vendor !== undefined) row.vendor = updates.vendor;
  if (updates.kategori !== undefined) row.kategori = updates.kategori;
  if (updates.uraian !== undefined) row.uraian = updates.uraian;
  if (updates.noInvoice !== undefined) row.no_invoice = updates.noInvoice;
  if (updates.nilaiTagihan !== undefined) row.nilai_tagihan = updates.nilaiTagihan;
  if (updates.totalDibayar !== undefined) row.total_dibayar = updates.totalDibayar;
  if (updates.sisaHutang !== undefined) row.sisa_hutang = updates.sisaHutang;
  if (updates.jatuhTempo !== undefined) row.jatuh_tempo = updates.jatuhTempo;
  if (updates.umurHari !== undefined) row.umur_hari = updates.umurHari;
  if (updates.status !== undefined) row.status = updates.status;
  if (updates.pic !== undefined) row.pic = updates.pic;
  if (updates.approval !== undefined) row.approval = updates.approval;
  if (updates.lampiran !== undefined) row.lampiran = updates.lampiran;
  if (updates.approvalStatus !== undefined) row.approval_status = updates.approvalStatus;
  if (updates.approvalComment !== undefined) row.approval_comment = updates.approvalComment;
  if (updates.approvedBy !== undefined) row.approved_by = updates.approvedBy;
  if (updates.approvedAt !== undefined) row.approved_at = updates.approvedAt;
  row.updated_at = new Date().toISOString();
  const { error } = await supabase.from('hutang').update(row).eq('id', id);
  if (error) throw new Error(error.message);
}

export async function deleteHutangDB(id: string): Promise<void> {
  const supabase = getSupabase();
  const { error } = await supabase.from('hutang').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

// ─── Pembayaran Hutang ────────────────────────────────────────────────────────

function rowToPembayaran(row: any): PembayaranHutang {
  return {
    id: row.id,
    hutangId: row.hutang_id,
    projectId: row.project_id,
    tanggalPembayaran: row.tanggal_pembayaran,
    vendor: row.vendor,
    nominal: row.nominal,
    bankKas: row.bank_kas,
    metode: row.metode,
    noBukti: row.no_bukti,
    lampiran: row.lampiran,
  };
}

function pembayaranToRow(p: PembayaranHutang) {
  return {
    id: p.id,
    hutang_id: p.hutangId,
    project_id: p.projectId,
    tanggal_pembayaran: p.tanggalPembayaran,
    vendor: p.vendor,
    nominal: p.nominal,
    bank_kas: p.bankKas,
    metode: p.metode,
    no_bukti: p.noBukti,
    lampiran: p.lampiran,
  };
}

export async function getPembayaranListDB(): Promise<PembayaranHutang[]> {
  const supabase = getSupabase();
  const { data, error } = await supabase.from('pembayaran_hutang').select('*').order('id');
  if (error) { console.error('getPembayaranListDB error:', error.message); return []; }
  return (data || []).map(rowToPembayaran);
}

export async function addPembayaranDB(p: PembayaranHutang): Promise<void> {
  const supabase = getSupabase();
  const { error } = await supabase.from('pembayaran_hutang').insert(pembayaranToRow(p));
  if (error) throw new Error(error.message);
}

export async function deletePembayaranDB(id: string): Promise<void> {
  const supabase = getSupabase();
  const { error } = await supabase.from('pembayaran_hutang').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

export async function updatePembayaranDB(id: string, p: Partial<PembayaranHutang>): Promise<void> {
  const supabase = getSupabase();
  const row: any = {};
  if (p.hutangId !== undefined) row.hutang_id = p.hutangId;
  if (p.projectId !== undefined) row.project_id = p.projectId;
  if (p.tanggalPembayaran !== undefined) row.tanggal_pembayaran = p.tanggalPembayaran;
  if (p.vendor !== undefined) row.vendor = p.vendor;
  if (p.nominal !== undefined) row.nominal = p.nominal;
  if (p.bankKas !== undefined) row.bank_kas = p.bankKas;
  if (p.metode !== undefined) row.metode = p.metode;
  if (p.noBukti !== undefined) row.no_bukti = p.noBukti;
  if (p.lampiran !== undefined) row.lampiran = p.lampiran;
  const { error } = await supabase.from('pembayaran_hutang').update(row).eq('id', id);
  if (error) throw new Error(error.message);
}

// ─── Piutang ──────────────────────────────────────────────────────────────────

function rowToPiutang(row: any): Piutang {
  return {
    id: row.id,
    projectId: row.project_id,
    subProjectId: row.sub_project_id,
    tanggal: row.tanggal,
    sumberDana: row.sumber_dana,
    pihak: row.pihak,
    uraian: row.uraian,
    nilaiPiutang: row.nilai_piutang,
    totalDiterima: row.total_diterima,
    sisaPiutang: row.sisa_piutang,
    jatuhTempo: row.jatuh_tempo,
    umurHari: row.umur_hari,
    status: row.status,
    pic: row.pic,
    lampiran: row.lampiran,
    approvalStatus: row.approval_status || 'Pending',
    approvalComment: row.approval_comment || '',
    approvedBy: row.approved_by || '',
    approvedAt: row.approved_at || '',
  };
}

function piutangToRow(p: Piutang) {
  return {
    id: p.id,
    project_id: p.projectId,
    sub_project_id: p.subProjectId,
    tanggal: p.tanggal,
    sumber_dana: p.sumberDana,
    pihak: p.pihak,
    uraian: p.uraian,
    nilai_piutang: p.nilaiPiutang,
    total_diterima: p.totalDiterima,
    sisa_piutang: p.sisaPiutang,
    jatuh_tempo: p.jatuhTempo,
    umur_hari: p.umurHari,
    status: p.status,
    pic: p.pic,
    lampiran: p.lampiran,
    approval_status: p.approvalStatus || 'Pending',
    approval_comment: p.approvalComment || null,
    approved_by: p.approvedBy || null,
    approved_at: p.approvedAt || null,
  };
}

export async function getPiutangListDB(): Promise<Piutang[]> {
  const supabase = getSupabase();
  const { data, error } = await supabase.from('piutang').select('*').order('id');
  if (error) { console.error('getPiutangListDB error:', error.message); return []; }
  return (data || []).map(rowToPiutang);
}

export async function addPiutangDB(p: Piutang): Promise<void> {
  const supabase = getSupabase();
  const { error } = await supabase.from('piutang').insert(piutangToRow(p));
  if (error) throw new Error(error.message);
}

export async function updatePiutangDB(id: string, updates: Partial<Piutang>): Promise<void> {
  const supabase = getSupabase();
  const row: any = {};
  if (updates.projectId !== undefined) row.project_id = updates.projectId;
  if (updates.subProjectId !== undefined) row.sub_project_id = updates.subProjectId;
  if (updates.tanggal !== undefined) row.tanggal = updates.tanggal;
  if (updates.sumberDana !== undefined) row.sumber_dana = updates.sumberDana;
  if (updates.pihak !== undefined) row.pihak = updates.pihak;
  if (updates.uraian !== undefined) row.uraian = updates.uraian;
  if (updates.nilaiPiutang !== undefined) row.nilai_piutang = updates.nilaiPiutang;
  if (updates.totalDiterima !== undefined) row.total_diterima = updates.totalDiterima;
  if (updates.sisaPiutang !== undefined) row.sisa_piutang = updates.sisaPiutang;
  if (updates.jatuhTempo !== undefined) row.jatuh_tempo = updates.jatuhTempo;
  if (updates.umurHari !== undefined) row.umur_hari = updates.umurHari;
  if (updates.status !== undefined) row.status = updates.status;
  if (updates.pic !== undefined) row.pic = updates.pic;
  if (updates.lampiran !== undefined) row.lampiran = updates.lampiran;
  if (updates.approvalStatus !== undefined) row.approval_status = updates.approvalStatus;
  if (updates.approvalComment !== undefined) row.approval_comment = updates.approvalComment;
  if (updates.approvedBy !== undefined) row.approved_by = updates.approvedBy;
  if (updates.approvedAt !== undefined) row.approved_at = updates.approvedAt;
  row.updated_at = new Date().toISOString();
  const { error } = await supabase.from('piutang').update(row).eq('id', id);
  if (error) throw new Error(error.message);
}

export async function deletePiutangDB(id: string): Promise<void> {
  const supabase = getSupabase();
  const { error } = await supabase.from('piutang').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

// ─── Penerimaan Piutang ───────────────────────────────────────────────────────

function rowToPenerimaan(row: any): PenerimaanPiutang {
  return {
    id: row.id,
    piutangId: row.piutang_id,
    projectId: row.project_id,
    tanggalPenerimaan: row.tanggal_penerimaan,
    pihak: row.pihak,
    nominal: row.nominal,
    bankKas: row.bank_kas,
    metode: row.metode,
    noBukti: row.no_bukti,
    lampiran: row.lampiran,
  };
}

function penerimaanToRow(p: PenerimaanPiutang) {
  return {
    id: p.id,
    piutang_id: p.piutangId,
    project_id: p.projectId,
    tanggal_penerimaan: p.tanggalPenerimaan,
    pihak: p.pihak,
    nominal: p.nominal,
    bank_kas: p.bankKas,
    metode: p.metode,
    no_bukti: p.noBukti,
    lampiran: p.lampiran,
  };
}

export async function getPenerimaanListDB(): Promise<PenerimaanPiutang[]> {
  const supabase = getSupabase();
  const { data, error } = await supabase.from('penerimaan_piutang').select('*').order('id');
  if (error) { console.error('getPenerimaanListDB error:', error.message); return []; }
  return (data || []).map(rowToPenerimaan);
}

export async function addPenerimaanDB(p: PenerimaanPiutang): Promise<void> {
  const supabase = getSupabase();
  const { error } = await supabase.from('penerimaan_piutang').insert(penerimaanToRow(p));
  if (error) throw new Error(error.message);
}

export async function updatePenerimaanDB(id: string, updates: Partial<PenerimaanPiutang>): Promise<void> {
  const supabase = getSupabase();
  const row: any = {};
  if (updates.piutangId !== undefined) row.piutang_id = updates.piutangId;
  if (updates.projectId !== undefined) row.project_id = updates.projectId;
  if (updates.tanggalPenerimaan !== undefined) row.tanggal_penerimaan = updates.tanggalPenerimaan;
  if (updates.pihak !== undefined) row.pihak = updates.pihak;
  if (updates.nominal !== undefined) row.nominal = updates.nominal;
  if (updates.bankKas !== undefined) row.bank_kas = updates.bankKas;
  if (updates.metode !== undefined) row.metode = updates.metode;
  if (updates.noBukti !== undefined) row.no_bukti = updates.noBukti;
  if (updates.lampiran !== undefined) row.lampiran = updates.lampiran;
  row.updated_at = new Date().toISOString();
  const { error } = await supabase.from('penerimaan_piutang').update(row).eq('id', id);
  if (error) throw new Error(error.message);
}

export async function deletePenerimaanDB(id: string): Promise<void> {
  const supabase = getSupabase();
  const { error } = await supabase.from('penerimaan_piutang').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

// ─── Activity Log ─────────────────────────────────────────────────────────────

function rowToActivityLog(row: any): ActivityLog {
  return {
    id: row.id,
    timestamp: row.timestamp,
    action: row.action,
    module: row.module,
    description: row.description,
    refId: row.ref_id,
  };
}

export async function getActivityLogsDB(): Promise<ActivityLog[]> {
  const supabase = getSupabase();
  const { data, error } = await supabase.from('activity_logs').select('*').order('timestamp', { ascending: false });
  if (error) { console.error('getActivityLogsDB error:', error.message); return []; }
  return (data || []).map(rowToActivityLog);
}

export async function addActivityLogDB(entry: Omit<ActivityLog, 'id' | 'timestamp'>): Promise<void> {
  const supabase = getSupabase();
  const { data: existing } = await supabase.from('activity_logs').select('id').order('id', { ascending: false }).limit(1);
  const lastNum = existing && existing.length > 0
    ? parseInt(existing[0].id.replace('LOG-', ''), 10) || 0
    : 0;
  const newId = `LOG-${String(lastNum + 1).padStart(4, '0')}`;
  const { error } = await supabase.from('activity_logs').insert({
    id: newId,
    timestamp: new Date().toISOString(),
    action: entry.action,
    module: entry.module,
    description: entry.description,
    ref_id: entry.refId,
  });
  if (error) console.error('addActivityLogDB error:', error.message);
}

// ─── ID Generators ────────────────────────────────────────────────────────────

export async function generateHutangId(): Promise<string> {
  const supabase = getSupabase();
  const { data } = await supabase.from('hutang').select('id').order('id', { ascending: false }).limit(1);
  const lastNum = data && data.length > 0 ? parseInt(data[0].id.replace('HTG-', ''), 10) || 0 : 0;
  return `HTG-${String(lastNum + 1).padStart(3, '0')}`;
}

export async function generatePembayaranId(): Promise<string> {
  const supabase = getSupabase();
  const { data } = await supabase.from('pembayaran_hutang').select('id').order('id', { ascending: false }).limit(1);
  const lastNum = data && data.length > 0 ? parseInt(data[0].id.replace('PAY-', ''), 10) || 0 : 0;
  return `PAY-${String(lastNum + 1).padStart(3, '0')}`;
}

export async function generatePiutangId(): Promise<string> {
  const supabase = getSupabase();
  const { data } = await supabase.from('piutang').select('id').order('id', { ascending: false }).limit(1);
  const lastNum = data && data.length > 0 ? parseInt(data[0].id.replace('PGT-', ''), 10) || 0 : 0;
  return `PGT-${String(lastNum + 1).padStart(3, '0')}`;
}

export async function generatePenerimaanId(): Promise<string> {
  const supabase = getSupabase();
  const { data } = await supabase.from('penerimaan_piutang').select('id').order('id', { ascending: false }).limit(1);
  const lastNum = data && data.length > 0 ? parseInt(data[0].id.replace('RCV-', ''), 10) || 0 : 0;
  return `RCV-${String(lastNum + 1).padStart(3, '0')}`;
}

export async function generateProjectId(): Promise<string> {
  const supabase = getSupabase();
  const { data } = await supabase.from('projects').select('id').order('id', { ascending: false }).limit(1);
  const lastNum = data && data.length > 0 ? parseInt(data[0].id.replace('P-', ''), 10) || 0 : 0;
  return `P-${String(lastNum + 1).padStart(3, '0')}`;
}

export async function generateSubProjectId(projectId: string): Promise<string> {
  const supabase = getSupabase();
  const { data } = await supabase.from('sub_projects').select('id').eq('project_id', projectId).order('id', { ascending: false }).limit(1);
  const lastNum = data && data.length > 0
    ? parseInt(data[0].id.split('.')[1] || '0', 10) || 0
    : 0;
  return `${projectId}.${String(lastNum + 1).padStart(2, '0')}`;
}

// ─── Notifications ────────────────────────────────────────────────────────────

export interface Notification {
  id: string;
  userId: string;
  title: string;
  message: string;
  module: string;
  refId?: string;
  isRead: boolean;
  createdAt: string;
}

function rowToNotification(row: any): Notification {
  return {
    id: row.id,
    userId: row.user_id,
    title: row.title,
    message: row.message,
    module: row.module,
    refId: row.ref_id,
    isRead: row.is_read,
    createdAt: row.created_at,
  };
}

export async function getNotificationsDB(userId: string): Promise<Notification[]> {
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from('notifications')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(50);
  if (error) { console.error('getNotificationsDB error:', error.message); return []; }
  return (data || []).map(rowToNotification);
}

export async function getUnreadNotificationCountDB(userId: string): Promise<number> {
  const supabase = getSupabase();
  const { count, error } = await supabase
    .from('notifications')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('is_read', false);
  if (error) { console.error('getUnreadNotificationCountDB error:', error.message); return 0; }
  return count || 0;
}

export async function addNotificationDB(n: Omit<Notification, 'id' | 'createdAt' | 'isRead'>): Promise<void> {
  const supabase = getSupabase();
  const { data: existing } = await supabase
    .from('notifications')
    .select('id')
    .order('created_at', { ascending: false })
    .limit(1);
  const lastNum = existing && existing.length > 0
    ? parseInt(existing[0].id.replace('NOTIF-', ''), 10) || 0
    : 0;
  const newId = `NOTIF-${String(lastNum + 1).padStart(4, '0')}`;
  const { error } = await supabase.from('notifications').insert({
    id: newId,
    user_id: n.userId,
    title: n.title,
    message: n.message,
    module: n.module,
    ref_id: n.refId || null,
    is_read: false,
    created_at: new Date().toISOString(),
  });
  if (error) console.error('addNotificationDB error:', error.message);
}

export async function markNotificationsReadDB(userId: string): Promise<void> {
  const supabase = getSupabase();
  await supabase.from('notifications').update({ is_read: true }).eq('user_id', userId).eq('is_read', false);
}

export async function getHutangCreatorDB(hutangId: string): Promise<string | null> {
  // Returns the user_id of who created the hutang via activity log
  const supabase = getSupabase();
  const { data } = await supabase
    .from('activity_logs')
    .select('user_id')
    .eq('ref_id', hutangId)
    .eq('action', 'TAMBAH_HUTANG')
    .limit(1)
    .single();
  return data?.user_id || null;
}

export async function getPiutangCreatorDB(piutangId: string): Promise<string | null> {
  const supabase = getSupabase();
  const { data } = await supabase
    .from('activity_logs')
    .select('user_id')
    .eq('ref_id', piutangId)
    .eq('action', 'TAMBAH_PIUTANG')
    .limit(1)
    .single();
  return data?.user_id || null;
}
